import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import {
    isResolvedProblemDetails,
    qpxToast as toast,
} from '@quipex/core/utils';
import type { ActivityIntent } from '../activity-journal';
import { ActivityExportDownloader } from './export-downloader';
import { ActivityToastOwner } from './toast-owner';

export type ActivityActionExecution = 'downloaded' | 'navigated';

@Injectable()
export class ActivityActionExecutor {
    private readonly exportDownloads = inject(ActivityExportDownloader);
    private readonly router = inject(Router);
    private readonly toastOwner = inject(ActivityToastOwner);

    public async execute(
        intent: ActivityIntent
    ): Promise<ActivityActionExecution> {
        switch (intent.type) {
            case 'view-receipt':
                await this.navigateToReceipt(
                    intent.buildingPublicId,
                    intent.batchId
                );
                return 'navigated';
            case 'open-preview':
                await this.navigateToDocuments(intent.buildingPublicId, {
                    documentId: intent.documentPublicId,
                });
                return 'navigated';
            case 'download-selected-export': {
                return this.downloadWithFeedback((isCurrent) =>
                    this.exportDownloads.downloadSelectedDocuments({
                        buildingPublicId: intent.buildingPublicId,
                        jobId: intent.jobId,
                        isCurrent,
                    })
                );
            }
            case 'download-export': {
                return this.downloadWithFeedback((isCurrent) =>
                    this.exportDownloads.downloadDocuments({
                        buildingPublicId: intent.buildingPublicId,
                        jobId: intent.jobId,
                        isCurrent,
                    })
                );
            }
            case 'download-pack-export': {
                return this.downloadWithFeedback((isCurrent) =>
                    this.exportDownloads.downloadPack({
                        buildingPublicId: intent.buildingPublicId,
                        packId: intent.packId,
                        jobId: intent.jobId,
                        isCurrent,
                    })
                );
            }
        }
    }

    private async downloadWithFeedback(
        download: (isCurrent: () => boolean) => Promise<void>
    ): Promise<ActivityActionExecution> {
        const toastScope = this.toastOwner.createScope();
        const toastId = toastScope.own(toast.loading('Downloading ZIP...'));
        try {
            await download(toastScope.isCurrent);
            return 'downloaded';
        } finally {
            toastScope.dismiss(toastId);
            toastScope.destroy();
        }
    }

    private async navigateToDocuments(
        buildingId: string,
        queryParams?: Readonly<Record<string, string>>
    ): Promise<void> {
        const destination = `/buildings/${buildingId}/documents`;
        await this.router.navigate(['/buildings', buildingId, 'documents'], {
            queryParams,
        });

        if (!routeMatches(this.router.url, destination, queryParams)) {
            throw new ActivityUnavailableError();
        }
    }

    private async navigateToReceipt(
        buildingId: string,
        batchId: string
    ): Promise<void> {
        const destination = `/buildings/${buildingId}/documents/receipts/${batchId}`;
        await this.router.navigate([
            '/buildings',
            buildingId,
            'documents',
            'receipts',
            batchId,
        ]);

        if (!routeMatches(this.router.url, destination)) {
            throw new ActivityUnavailableError();
        }
    }
}

export function isUnavailableActivityActionError(error: unknown): boolean {
    if (error instanceof ActivityUnavailableError) return true;

    const status =
        error instanceof HttpErrorResponse || isResolvedProblemDetails(error)
            ? error.status
            : null;
    return status === 403 || status === 404 || status === 410;
}

export function activityActionFailureCategory(error: unknown): string {
    if (isUnavailableActivityActionError(error)) return 'unavailable';
    if (error instanceof HttpErrorResponse || isResolvedProblemDetails(error)) {
        return 'http';
    }
    return error instanceof Error ? 'client' : 'unknown';
}

class ActivityUnavailableError extends Error {
    public constructor() {
        super('The Activity destination is unavailable.');
    }
}

function routeMatches(
    currentUrl: string,
    destination: string,
    queryParams?: Readonly<Record<string, string>>
): boolean {
    const current = new URL(currentUrl, 'https://activity.local');
    if (current.pathname.replace(/\/$/, '') !== destination) return false;

    return Object.entries(queryParams ?? {}).every(
        ([name, value]) => current.searchParams.get(name) === value
    );
}
